import type {
  Api_LiveStream_API_Event,
  Api_LiveStream_Chat_Message,
  Api_LiveStream_Event,
  Api_LiveStream_Message,
  Api_Product,
  Api_Video,
} from '@core/api'
import {
  Api_Video_Type,
  Enum_LiveStream_Auto_Response,
  Enum_LiveStream_Private_Messaging,
  Enum_LiveStream_Provider,
  Enum_LiveStream_Status,
} from '@core/api'
import { ILiveStreamState } from '@player/Player3/store/types'

/** Simple check for video type "live_stream" */
export const isLiveStream = (video: Api_Video | null): boolean =>
  video?.video_type === Api_Video_Type.LIVESTREAM

/**
 * Resolve the URL a livestream replay plays from. `/detail` serves
 * `replay_filtered_url` for replays that have a filtered rendition; replays
 * created before that field existed only carry `replay_url`.
 */
export const getLiveStreamReplayUrl = (
  video: Api_Video | null,
): string | null =>
  video?.live_stream_replay_filtered_url ||
  video?.live_stream_replay_url ||
  null

/** Determine whether video is a valid livestream replay (based on status and `replay_enabled` flag) */
export function isLiveStreamReplay(video: Api_Video | null): boolean {
  return (
    video?.live_stream_status === Enum_LiveStream_Status.REPLAY &&
    !!video.live_stream_replay_enabled &&
    !!getLiveStreamReplayUrl(video)
  )
}

/** Determine whether video is a livestream replay with `replay_enabled` flag disabled */
export function isLivestreamReplayDisabled(video: Api_Video | null): boolean {
  return (
    video?.live_stream_status === Enum_LiveStream_Status.REPLAY &&
    !video.live_stream_replay_enabled
  )
}

/**
 * Determine whether the video is an active livestream (based on status).
 * [CS-9765] We also check if the video has a valid playback URL or a valid replay
 * URL with CDN provider.
 */
export function isLiveStreamLive(video: Api_Video | null): boolean {
  return (
    (video?.live_stream_status === Enum_LiveStream_Status.ACTIVE ||
      video?.live_stream_status === Enum_LiveStream_Status.PAUSED) &&
    (!!video?.live_stream_playback_url ||
      (!!video?.live_stream_replay_url &&
        video?.live_stream_provider === Enum_LiveStream_Provider.CDN))
  )
}

/** Determine whether the video is active or replay livestream */
export function isLiveStreamLiveOrReplay(video: Api_Video | null): boolean {
  return isLiveStreamLive(video) || isLiveStreamReplay(video)
}

/** Determine whether the livestream status is IDLE (not live yet) */
export function isLiveStreamIdle(video: Api_Video | null): boolean {
  return video?.live_stream_status === Enum_LiveStream_Status.IDLE
}

/** Determine whether the video is a re-stream of a live stream or another re-stream */
export function isLiveStreamRestream(video: Api_Video | null): boolean {
  return !!video?.live_stream_video_source
}

/**
 * Check if livestream switching between public/private messaging is enabled
 */
export const isLiveStreamPrivatePublicSwitchAvailable = (
  video: Api_Video | null,
): boolean => {
  return Boolean(
    video &&
    !(
      video.live_stream_auto_response !== Enum_LiveStream_Auto_Response.DISABLED
    ) &&
    ((video.live_stream_private_messaging ===
      Enum_LiveStream_Private_Messaging.ALWAYS_ON &&
      isLiveStreamLiveOrReplay(video)) ||
      (video.live_stream_private_messaging ===
        Enum_LiveStream_Private_Messaging.REPLAY_ONLY &&
        isLiveStreamReplay(video))),
  )
}

/**
 * Livestream is idle (has not started yet, connection status is unknown)
 */
export const isStreamingIdle = (
  video: Api_Video | null,
  liveStreamState: ILiveStreamState | null,
): boolean =>
  // When the live stream is idle, the live stream state is not set.
  isLiveStream(video) &&
  liveStreamState === null &&
  video?.live_stream_status === Enum_LiveStream_Status.IDLE

/**
 * Livestream is live (connecting, playing, paused - based on player state)
 */
export const isStreamingLive = (
  liveStreamState: ILiveStreamState | null,
): boolean =>
  liveStreamState !== null &&
  [
    ILiveStreamState.ACTIVE_WAITING,
    ILiveStreamState.ACTIVE_PLAYING,
    ILiveStreamState.ACTIVE_PAUSED,
  ].includes(liveStreamState)

/**
 * Livestream is live (connected and playing or paused - based on player state)
 */
export const isStreamingLivePlaying = (
  liveStreamState: ILiveStreamState | null,
): boolean =>
  liveStreamState !== null &&
  [ILiveStreamState.ACTIVE_PLAYING, ILiveStreamState.ACTIVE_PAUSED].includes(
    liveStreamState,
  )

/**
 * Livestream replay is playing (based on player state)
 */
export const isStreamingReplay = (
  liveStreamState: ILiveStreamState | null,
): boolean =>
  liveStreamState !== null &&
  ILiveStreamState.REPLAY_PLAYING === liveStreamState

/**
 *
 */
export const isStreamingActiveOrReplay = (
  video: Api_Video | null,
  liveStreamState: ILiveStreamState | null,
): boolean => {
  return (
    isLiveStream(video) &&
    (isStreamingLive(liveStreamState) || isStreamingReplay(liveStreamState))
  )
}

/**
 * Checks if the video is being restreamed (used for scheduled/recurring showrooms).
 *
 * @param {Api_Video | null} video - The video object.
 * @param {ILiveStreamState | null} liveStreamState - The current livestream state.
 * @returns {boolean} - Returns true if the video is being restreamed; otherwise, false.
 */
export const isRestreaming = (
  video: Api_Video | null,
  liveStreamState: ILiveStreamState | null,
): boolean => {
  return (
    isLiveStream(video) &&
    !!video?.ref_video_id &&
    isStreamingLive(liveStreamState)
  )
}

/**
 * Transform a chat message from API format to a WS (and stored) live stream message
 */
export const chatMessageToLiveStreamMessage = (
  chatMessage: Api_LiveStream_Chat_Message,
): Api_LiveStream_Message => {
  const {
    id,
    text,
    user_type,
    username,
    elapsed_time,
    reply,
    is_nsfw,
    inserted_at,
  } = chatMessage

  return {
    event_type: 'message',
    id,
    is_nsfw,
    text,
    user_type,
    username,
    elapsed_time,
    reply,
    inserted_at,
  }
}

/**
 * Transform a private chat message from API format to a WS (and stored) live stream message
 */
export const chatPrivateMessageToLiveStreamPrivateMessage = (
  chatMessage: Api_LiveStream_Chat_Message,
): Api_LiveStream_Message => {
  const { id, text, user_type, username, elapsed_time, reply } = chatMessage

  return {
    event_type: 'private_message',
    id,
    text,
    user_type,
    username,
    elapsed_time,
    reply,
  }
}

/**
 *
 */
export const eventToLiveStreamEvent = (
  event: Api_LiveStream_API_Event,
): Api_LiveStream_Event => {
  const { action_id, action_type, elapsed_time, user_type, payload } = event

  return {
    event_type: action_type,
    id: action_id,
    payload,
    user_type,
    elapsed_time,
  }
}

/**
 * Sort messages by elapsed time (descending)
 */
export const byElapsedTimeDesc = (
  a: Api_LiveStream_Message,
  b: Api_LiveStream_Message,
): number => {
  if (a.elapsed_time === undefined || b.elapsed_time === undefined) {
    return -1
  }
  return a.elapsed_time >= b.elapsed_time ? 1 : -1
}

/** Sort messages by inserted_at (descending) */
export const byInsertedAtDesc = (
  a: Api_LiveStream_Message,
  b: Api_LiveStream_Message,
): number => {
  if (a.inserted_at === undefined || b.inserted_at === undefined) {
    return -1
  }
  return new Date(a.inserted_at).getTime() >= new Date(b.inserted_at).getTime()
    ? 1
    : -1
}

/** Determine whether the product is unavailable for purchase within a ls replay */
export const isVideoReplayAndProductUnavailable = (
  video: Api_Video | null,
  product: Api_Product,
): boolean =>
  isLiveStreamReplay(video) &&
  product.available_for_live_stream_replay === false
